import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AdminButtonComponent,
  AdminConfirmDialogService,
  AdminIconComponent,
  AdminPageHeaderComponent,
  AdminTableComponent,
  AdminToastService,
} from '../../../shared/admin';
import {
  PLATFORM_SIDEBAR_MENU,
  isPlatformMenuGroup,
  isPlatformMenuSection,
  type PlatformMenuGroup,
  type PlatformMenuLink,
} from '../../permissions/platform-menu.registry';
import { getPermissionModule, highestAllowedLevel } from '../../permissions/platform-permission.registry';
import type { PermissionLevel, RoleModulePermission } from '../../permissions/permission.types';
import { PERMISSION_LEVEL_NO_ACCESS } from '../../permissions/permission.types';
import { permissionLevelLabel } from '../../permissions/permission-level.util';
import { PermissionService } from '../../permissions/permission.service';
import {
  RolePermissionsApiService,
  type RolePermissionDetail,
} from '../../permissions/role-permissions-api.service';

const ROLE_OPTIONS = [
  { key: 'SUPER_ADMIN', label: 'Super Admin', locked: true },
  { key: 'PLATFORM_STAFF', label: 'Platform Staff', locked: false },
  { key: 'COUNSELLOR', label: 'Counsellor', locked: false },
] as const;

type ModuleTreeRow =
  | {
      kind: 'group';
      groupKey: string;
      label: string;
      expanded: boolean;
      childCount: number;
    }
  | {
      kind: 'module';
      moduleKey: string;
      label: string;
      indent: boolean;
      accessOnly: boolean;
    };

type ActionTreeRow =
  | {
      kind: 'module';
      moduleKey: string;
      label: string;
      expanded: boolean;
      actionCount: number;
    }
  | {
      kind: 'action';
      moduleKey: string;
      actionKey: string;
      actionLabel: string;
      allowedLevels: PermissionLevel[];
    };

@Component({
  selector: 'app-roles-permissions-page',
  standalone: true,
  imports: [
    FormsModule,
    AdminPageHeaderComponent,
    AdminButtonComponent,
    AdminTableComponent,
    AdminIconComponent,
  ],
  templateUrl: './roles-permissions-page.component.html',
  styleUrl: './roles-permissions-page.component.scss',
})
export class RolesPermissionsPageComponent {
  private readonly api = inject(RolePermissionsApiService);
  private readonly toast = inject(AdminToastService);
  private readonly confirm = inject(AdminConfirmDialogService);
  private readonly permissions = inject(PermissionService);

  readonly showEdit = computed(() => this.permissions.canShow('staff.rolePermissions', 'edit'));
  readonly canEdit = computed(() =>
    this.permissions.canExecute('staff.rolePermissions', 'edit'),
  );

  readonly roles = ROLE_OPTIONS;
  readonly selectedRoleKey = signal<(typeof ROLE_OPTIONS)[number]['key']>('PLATFORM_STAFF');
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly workingCopy = signal<RoleModulePermission[]>([]);
  readonly baselineJson = signal('');
  readonly search = signal('');
  /** Which permission area tab is visible: modules | actions */
  readonly activeViewTab = signal<'modules' | 'actions'>('modules');

  /** Independent expand state for Module Access groups (groupKey → expanded). */
  private readonly moduleExpanded = signal<Record<string, boolean>>({});
  /** Independent expand state for Action Permissions modules (moduleKey → expanded). */
  private readonly actionExpanded = signal<Record<string, boolean>>({});

  readonly isLocked = computed(() => this.selectedRoleKey() === 'SUPER_ADMIN');
  readonly dirty = computed(() => JSON.stringify(this.workingCopy()) !== this.baselineJson());

  readonly selectedRoleLabel = computed(() => {
    const key = this.selectedRoleKey();
    return this.roles.find((r) => r.key === key)?.label ?? key;
  });

  private readonly menuGroups = computed(() =>
    PLATFORM_SIDEBAR_MENU.filter(isPlatformMenuGroup),
  );

  readonly moduleTreeRows = computed((): ModuleTreeRow[] => {
    const q = this.search().trim().toLowerCase();
    const expanded = this.moduleExpanded();
    const rows: ModuleTreeRow[] = [];

    for (const item of PLATFORM_SIDEBAR_MENU) {
      if (isPlatformMenuSection(item)) continue;

      if (isPlatformMenuGroup(item)) {
        const matchingChildren = this.filterGroupChildren(item, q);
        if (matchingChildren.length === 0) continue;

        const forceOpen = !!q;
        const isOpen = forceOpen || expanded[item.groupKey] === true;
        rows.push({
          kind: 'group',
          groupKey: item.groupKey,
          label: item.label,
          expanded: isOpen,
          childCount: matchingChildren.length,
        });
        if (isOpen) {
          for (const c of matchingChildren) {
            rows.push(this.toModuleRow(c, true));
          }
        }
        continue;
      }

      if (q && !this.matchesQuery(item.label, item.moduleKey, q)) continue;
      rows.push(this.toModuleRow(item, false));
    }
    return rows;
  });

  readonly actionTreeRows = computed((): ActionTreeRow[] => {
    const q = this.search().trim().toLowerCase();
    const expanded = this.actionExpanded();
    const rows: ActionTreeRow[] = [];

    for (const m of this.workingCopy()) {
      if (!m.access) continue;
      const def = getPermissionModule(m.moduleKey);
      if (!def || def.kind !== 'module' || def.accessOnly) continue;

      const matchingActions = def.actions.filter(
        (a) =>
          !q ||
          this.matchesQuery(def.label, m.moduleKey, q) ||
          this.matchesQuery(a.label, a.key, q),
      );
      if (q && matchingActions.length === 0 && !this.matchesQuery(def.label, m.moduleKey, q)) {
        continue;
      }

      const actions = q ? matchingActions : def.actions;
      if (actions.length === 0) continue;

      const forceOpen = !!q;
      const isOpen = forceOpen || expanded[m.moduleKey] === true;
      rows.push({
        kind: 'module',
        moduleKey: m.moduleKey,
        label: def.label,
        expanded: isOpen,
        actionCount: actions.length,
      });
      if (isOpen) {
        for (const action of actions) {
          rows.push({
            kind: 'action',
            moduleKey: m.moduleKey,
            actionKey: action.key,
            actionLabel: action.label,
            allowedLevels: action.allowedLevels,
          });
        }
      }
    }
    return rows;
  });

  readonly moduleGroupKeys = computed(() => this.menuGroups().map((g) => g.groupKey));

  readonly actionModuleKeys = computed(() =>
    this.workingCopy()
      .filter((m) => m.access)
      .map((m) => m.moduleKey)
      .filter((key) => {
        const def = getPermissionModule(key);
        return !!def && def.kind === 'module' && !def.accessOnly && def.actions.length > 0;
      }),
  );

  constructor() {
    void this.loadRole(this.selectedRoleKey());
  }

  async selectRole(roleKey: (typeof ROLE_OPTIONS)[number]['key']): Promise<void> {
    if (roleKey === this.selectedRoleKey()) return;
    if (this.dirty()) {
      const ok = await this.confirm.confirm({
        title: 'Discard changes?',
        message: 'You have unsaved changes for the current role. Discard and switch roles?',
        confirmLabel: 'Discard',
        confirmVariant: 'danger',
      });
      if (!ok) return;
    }
    this.selectedRoleKey.set(roleKey);
    await this.loadRole(roleKey);
  }

  async loadRole(roleKey: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    this.api.getByRoleKey(roleKey).subscribe({
      next: (data) => {
        this.applyDetail(data);
        this.loading.set(false);
      },
      error: (err: { error?: { message?: string } }) => {
        this.loading.set(false);
        this.error.set(err.error?.message ?? 'Failed to load role permissions.');
      },
    });
  }

  private applyDetail(data: RolePermissionDetail): void {
    const modules = structuredClone(data.modules);
    this.workingCopy.set(modules);
    this.baselineJson.set(JSON.stringify(modules));
  }

  isAccessOn(moduleKey: string): boolean {
    return this.workingCopy().find((m) => m.moduleKey === moduleKey)?.access === true;
  }

  setAccess(moduleKey: string, access: boolean): void {
    if (this.isLocked()) return;
    const def = getPermissionModule(moduleKey);
    this.workingCopy.update((rows) =>
      rows.map((r) => {
        if (r.moduleKey !== moduleKey) return r;
        if (!access) {
          const actions: Record<string, PermissionLevel> = { ...r.actions };
          for (const action of def?.actions ?? []) {
            actions[action.key] = PERMISSION_LEVEL_NO_ACCESS;
          }
          return { ...r, access: false, actions };
        }
        // Turning Access ON: default each action to max allowed (Execute, or Read Only if no Execute).
        const actions: Record<string, PermissionLevel> = { ...r.actions };
        for (const action of def?.actions ?? []) {
          actions[action.key] = highestAllowedLevel(action);
        }
        return { ...r, access: true, actions };
      }),
    );
  }

  getLevel(moduleKey: string, actionKey: string): PermissionLevel {
    return this.workingCopy().find((m) => m.moduleKey === moduleKey)?.actions?.[actionKey] ?? 'NO_ACCESS';
  }

  setLevel(moduleKey: string, actionKey: string, level: PermissionLevel): void {
    if (this.isLocked()) return;
    this.workingCopy.update((rows) =>
      rows.map((r) => {
        if (r.moduleKey !== moduleKey) return r;
        return { ...r, actions: { ...r.actions, [actionKey]: level } };
      }),
    );
  }

  levelLabel(level: PermissionLevel): string {
    return permissionLevelLabel(level);
  }

  toggleModuleGroup(groupKey: string): void {
    this.moduleExpanded.update((state) => ({
      ...state,
      [groupKey]: !state[groupKey],
    }));
  }

  toggleActionModule(moduleKey: string): void {
    this.actionExpanded.update((state) => ({
      ...state,
      [moduleKey]: !state[moduleKey],
    }));
  }

  expandAllModules(): void {
    const next: Record<string, boolean> = {};
    for (const key of this.moduleGroupKeys()) next[key] = true;
    this.moduleExpanded.set(next);
  }

  collapseAllModules(): void {
    const next: Record<string, boolean> = {};
    for (const key of this.moduleGroupKeys()) next[key] = false;
    this.moduleExpanded.set(next);
  }

  expandAllActions(): void {
    const next: Record<string, boolean> = {};
    for (const key of this.actionModuleKeys()) next[key] = true;
    this.actionExpanded.set(next);
  }

  collapseAllActions(): void {
    const next: Record<string, boolean> = {};
    for (const key of this.actionModuleKeys()) next[key] = false;
    this.actionExpanded.set(next);
  }

  trackModuleRow(row: ModuleTreeRow): string {
    return row.kind === 'group' ? `g:${row.groupKey}` : `m:${row.moduleKey}`;
  }

  trackActionRow(row: ActionTreeRow): string {
    return row.kind === 'module' ? `am:${row.moduleKey}` : `a:${row.moduleKey}.${row.actionKey}`;
  }

  async cancel(): Promise<void> {
    await this.loadRole(this.selectedRoleKey());
  }

  async save(): Promise<void> {
    if (this.isLocked() || !this.dirty()) return;
    const roleKey = this.selectedRoleKey();
    const reducing =
      roleKey === 'PLATFORM_STAFF' &&
      this.baselineJson() !== '' &&
      this.countGrants(this.workingCopy()) <
        this.countGrants(JSON.parse(this.baselineJson()) as RoleModulePermission[]);

    if (reducing) {
      const ok = await this.confirm.confirm({
        title: 'Reduce permissions?',
        message: `You are reducing permissions for ${roleKey}. Existing users with this role may lose access. Continue?`,
        confirmLabel: 'Save changes',
        confirmVariant: 'danger',
      });
      if (!ok) return;
    }

    this.saving.set(true);
    this.error.set(null);
    this.api.updateByRoleKey(roleKey, this.workingCopy()).subscribe({
      next: (data) => {
        this.applyDetail(data);
        this.saving.set(false);
        this.toast.success(`Permissions saved for ${roleKey}`);
      },
      error: (err: { error?: { message?: string } }) => {
        this.saving.set(false);
        this.error.set(err.error?.message ?? 'Failed to save permissions.');
        this.toast.error(this.error()!);
      },
    });
  }

  private toModuleRow(item: PlatformMenuLink, indent: boolean): ModuleTreeRow {
    const def = getPermissionModule(item.moduleKey);
    return {
      kind: 'module',
      moduleKey: item.moduleKey,
      label: item.label,
      indent,
      accessOnly: def?.accessOnly === true,
    };
  }

  private filterGroupChildren(group: PlatformMenuGroup, q: string): PlatformMenuLink[] {
    if (!q) return group.children;
    const groupMatch = this.matchesQuery(group.label, group.groupKey, q);
    if (groupMatch) return group.children;
    return group.children.filter((c) => this.matchesQuery(c.label, c.moduleKey, q));
  }

  private matchesQuery(label: string, key: string, q: string): boolean {
    return label.toLowerCase().includes(q) || key.toLowerCase().includes(q);
  }

  private countGrants(modules: RoleModulePermission[]): number {
    let n = 0;
    for (const m of modules) {
      if (m.access) n += 1;
      for (const level of Object.values(m.actions ?? {})) {
        if (level !== 'NO_ACCESS') n += 1;
      }
    }
    return n;
  }
}
